import { JSX, useCallback, useState } from "react";

import Grid from "@mui/material/Grid";
import Rating, { IconContainerProps } from "@mui/material/Rating";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";

import { RatingField } from "../FieldService";
import {
	InformationRight,
	ReportFieldRights
} from "core/blueprint/trajectory/TrajectoryBlueprintService";

export type RatingInputProps = {
	field: RatingField;
	rights: ReportFieldRights;
	editable: boolean;
	onChange: (uuid: string, value: string) => void;
};

function RatingInput(props: RatingInputProps): JSX.Element {
	const [hover, setHover] = useState(-1);

	function getRating(): number | null {
		if (props.editable && hover > 0) {
			return hover;
		} else if (props.field.value !== null) {
			return props.field.value;
		} else {
			return null;
		}
	}

	function getScoreDescription(): string {
		const value = getRating();
		if (value === null) {
			return "";
		}

		return props.field.info[value - 1].description;
	}

	function getScoreName(): string {
		const value = getRating();
		if (value === null) {
			return "";
		}

		return props.field.info[value - 1].name;
	}

	function getScoreColour(): string {
		const value = getRating();
		if (value === null) {
			return "";
		}

		return props.field.info[value - 1].colour;
	}

	const createIconContainer = useCallback(
		(containerProps: IconContainerProps): JSX.Element => {
			const { value, ...other } = containerProps;

			const rating = getRating();
			let icon: JSX.Element;
			if (rating === null || value > rating) {
				icon = <StarBorderIcon />;
			} else {
				const colour = getScoreColour();
				icon = <StarIcon sx={{ color: colour }} />;
			}

			return <span {...other}>{icon}</span>;
		},
		[props.editable, props.field, hover]
	);

	const onChangeValue = useCallback(
		(_event: React.SyntheticEvent, value: number | null): void => {
			setHover(-1);
			if (value === null) {
				props.onChange(props.field.uuid, "");
			} else {
				props.onChange(props.field.uuid, value.toString());
			}
		},
		[props.onChange]
	);

	const onChangeHover = useCallback(
		(_event: React.SyntheticEvent, value: number): void => {
			setHover(value);
		},
		[]
	);

	const onResetHover = useCallback((): void => {
		setHover(-1);
	}, []);

	function toId(uuid: string): string {
		const separatorIndex = uuid.indexOf("#");
		return separatorIndex === -1 ? uuid : uuid.substring(0, separatorIndex);
	}

	return (
		<Grid item>
			<Typography>{props.field.name}</Typography>
			<Tooltip title={getScoreDescription()} placement="right">
				<div
					style={{ display: "inline-block" }}
					onMouseLeave={onResetHover}>
					<Rating
						disabled={
							!props.editable ||
							props.rights[toId(props.field.uuid)][
								InformationRight.WRITE
							] === false
						}
						value={props.field.value}
						max={props.field.info.length}
						IconContainerComponent={createIconContainer}
						onChange={onChangeValue}
						onChangeActive={onChangeHover}
					/>
				</div>
			</Tooltip>
			<Typography sx={{ minHeight: theme => theme.spacing(3) }}>
				{getScoreName()}
			</Typography>
		</Grid>
	);
}

export default RatingInput;
